import { isLocalEnvironment, isTestEnvironment } from '@/environment'

const GTM_ID = 'GTM-WH2S9ZFM'
const AW_ID = 'AW-17483545079'
export const AW_CONVERSION_SEND_TO = 'AW-17483545079/ozEwCNzbi4cbEPfz5pBB'
export const GOOGLE_ADS_LEAD_CONVERSION_VALUE_EUR = 139
export const GOOGLE_ADS_CONVERSION_REDIRECT_TIMEOUT_MS = 2000
// Keep in sync with frontend/src/shared/lib/openaiAdsPixel.ts (separate package; no shared import).
const OPENAI_ADS_PIXEL_ID = 'FUr96cbpUmJ1dMtkHqu1ck'
const OPENAI_ADS_SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js'

let marketingEnabled = false
let marketingScriptsInjected = false
let openAiAdsInitialized = false
let googleConsentDefaultPushed = false

type OaiqFn = {
    (...args: unknown[]): void
    q: unknown[]
}

type GoogleAdsConversionParams = {
    userType?: string
    redirectUrl?: string
}

type GoogleConsentState = 'granted' | 'denied'

type GoogleConsentUpdate = {
    ad_storage?: GoogleConsentState
    ad_user_data?: GoogleConsentState
    ad_personalization?: GoogleConsentState
    analytics_storage?: GoogleConsentState
}

let lastAnalyticsConsent: GoogleConsentState = 'denied'
let lastAdsConsent: GoogleConsentState = 'denied'

function pushGoogleConsentDefault(): void {
    if (googleConsentDefaultPushed) return
    googleConsentDefaultPushed = true
    ensureGtagStub()
    // Must go through window.gtag so dataLayer gets Arguments (not Array) — Consent Mode ignores Array pushes.
    window.gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        wait_for_update: 500,
    })
}

function pushGoogleConsentUpdate(update: GoogleConsentUpdate): void {
    ensureGtagStub()
    if (update.analytics_storage != null) lastAnalyticsConsent = update.analytics_storage
    if (update.ad_storage != null) lastAdsConsent = update.ad_storage
    window.gtag('consent', 'update', update)
}

function reassertGoogleConsentAfterTagsLoad(): void {
    window.gtag('consent', 'update', {
        analytics_storage: lastAnalyticsConsent,
        ad_storage: lastAdsConsent,
        ad_user_data: lastAdsConsent,
        ad_personalization: lastAdsConsent,
    })
}

function fireGoogleAdsLeadConversion(params: GoogleAdsConversionParams): void {
    const conversionPayload: Record<string, unknown> = {
        send_to: AW_CONVERSION_SEND_TO,
        value: GOOGLE_ADS_LEAD_CONVERSION_VALUE_EUR,
        currency: 'EUR',
        userType: params.userType,
    }

    if (!params.redirectUrl) {
        window.gtag('event', 'conversion', conversionPayload)
        return
    }

    let didRedirect = false
    const redirect = () => {
        if (didRedirect) return
        didRedirect = true
        window.location.href = params.redirectUrl
    }

    conversionPayload.event_callback = redirect
    window.gtag('event', 'conversion', conversionPayload)
    window.setTimeout(redirect, GOOGLE_ADS_CONVERSION_REDIRECT_TIMEOUT_MS)
}

/** Fires the Google Ads lead conversion when marketing consent is on. Optional redirect waits for gtag (with timeout). */
export function reportGoogleAdsLeadConversion(params: GoogleAdsConversionParams = {}): void {
    if (!marketingEnabled) {
        if (params.redirectUrl) {
            window.location.href = params.redirectUrl
        }
        return
    }

    ensureGtagStub()
    if (!marketingScriptsInjected) {
        injectMarketingScriptsNow()
    }

    fireGoogleAdsLeadConversion(params)
}

function ensureGtagStub(): void {
    window.dataLayer = window.dataLayer || []
    if (!window.gtag) {
        // Match the official gtag snippet: push `arguments`, not a rest-params Array.
        // Google Consent Mode / gtag.js only treat Arguments-style commands as consent.
        window.gtag = function gtag() {
            // eslint-disable-next-line prefer-rest-params -- gtag requires Arguments object, not Array
            const command = arguments[0]
            if (command !== 'consent' && !marketingEnabled) return
            // eslint-disable-next-line prefer-rest-params -- gtag requires Arguments object, not Array
            window.dataLayer.push(arguments)
        }
    }

    if (!window.gtag_report_conversion_and_redirect) {
        window.gtag_report_conversion_and_redirect = params => {
            reportGoogleAdsLeadConversion({
                userType: params?.userType,
                redirectUrl: params?.redirectUrl,
            })
            return false
        }
    }
}

function ensureOaiqStub(): void {
    if (window.oaiq) return

    const queue: unknown[] = []
    const oaiq = ((...args: unknown[]) => {
        queue.push(args)
    }) as OaiqFn
    oaiq.q = queue
    window.oaiq = oaiq
}

function ensureOpenAiAdsPixel(): void {
    ensureOaiqStub()

    if (!openAiAdsInitialized) {
        window.oaiq('consent', marketingEnabled)
        if (!marketingEnabled) return

        window.oaiq('init', {
            pixelId: OPENAI_ADS_PIXEL_ID,
            debug: isLocalEnvironment || isTestEnvironment,
        })
        openAiAdsInitialized = true
        return
    }

    window.oaiq('consent', marketingEnabled)
}

function injectGtagScript(): void {
    ensureGtagStub()
    window.gtag('js', new Date())
    window.gtag('config', AW_ID)

    const existing = document.querySelector(`script[src*="gtag/js?id=${AW_ID}"]`)
    if (existing) {
        reassertGoogleConsentAfterTagsLoad()
        return
    }

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${AW_ID}`
    script.onload = () => reassertGoogleConsentAfterTagsLoad()
    document.head.appendChild(script)
}

function injectGtm(): void {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

    const existing = document.querySelector(`script[src*="gtm.js?id=${GTM_ID}"]`)
    if (existing) {
        reassertGoogleConsentAfterTagsLoad()
        return
    }

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
    script.onload = () => reassertGoogleConsentAfterTagsLoad()
    document.head.appendChild(script)

    if (!document.getElementById('gtm-noscript')) {
        const noscript = document.createElement('noscript')
        noscript.id = 'gtm-noscript'
        noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
        document.body.insertBefore(noscript, document.body.firstChild)
    }
}

function injectOpenAiAdsScript(): void {
    ensureOpenAiAdsPixel()
    if (!marketingEnabled) return
    if (document.querySelector(`script[src="${OPENAI_ADS_SDK_URL}"]`)) return

    const script = document.createElement('script')
    script.async = true
    script.src = OPENAI_ADS_SDK_URL
    document.head.appendChild(script)
}

function runWhenIdle(task: () => void): void {
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => task(), { timeout: 3000 })
        return
    }
    window.setTimeout(task, 1)
}

function injectMarketingScriptsNow(): void {
    if (marketingScriptsInjected || typeof window === 'undefined' || !marketingEnabled) return
    ensureGtagStub()
    ensureOpenAiAdsPixel()
    injectGtagScript()
    injectGtm()
    injectOpenAiAdsScript()
    marketingScriptsInjected = true
}

function injectMarketingScripts(): void {
    if (marketingScriptsInjected || typeof window === 'undefined') return
    runWhenIdle(() => {
        injectMarketingScriptsNow()
    })
}

/** Installs gtag stubs + Consent Mode defaults (no network). Call once at bootstrap. */
export function installGtagStubs(): void {
    if (typeof window === 'undefined') return
    ensureGtagStub()
    pushGoogleConsentDefault()
}

/** Syncs analytics_storage for Google Consent Mode (Mixpanel banner measurement). */
export function applyAnalyticsConsent(enabled: boolean): void {
    if (typeof window === 'undefined') return
    ensureGtagStub()
    pushGoogleConsentDefault()
    pushGoogleConsentUpdate({
        analytics_storage: enabled ? 'granted' : 'denied',
    })
}

export function applyMarketingConsent(enabled: boolean): void {
    marketingEnabled = enabled
    if (typeof window !== 'undefined') {
        ensureGtagStub()
        pushGoogleConsentDefault()
        pushGoogleConsentUpdate({
            ad_storage: enabled ? 'granted' : 'denied',
            ad_user_data: enabled ? 'granted' : 'denied',
            ad_personalization: enabled ? 'granted' : 'denied',
        })
        ensureOpenAiAdsPixel()
    }
    if (enabled) {
        injectMarketingScripts()
    }
}

export function trackMarketingEvent(eventName: string, params?: Record<string, unknown>): void {
    if (!marketingEnabled || !marketingScriptsInjected) return
    ensureGtagStub()
    window.gtag('event', eventName, params)
}

export function trackOpenAiAdsPageViewed(page: { id: string; name: string }): void {
    if (!marketingEnabled || typeof window === 'undefined') return
    ensureOpenAiAdsPixel()
    window.oaiq('measure', 'page_viewed', {
        type: 'contents',
        contents: [
            {
                id: page.id,
                name: page.name,
                content_type: 'page',
            },
        ],
    })
}

export function trackOpenAiAdsLeadCreated(): void {
    if (!marketingEnabled || typeof window === 'undefined') return
    ensureOpenAiAdsPixel()
    window.oaiq('measure', 'lead_created', {
        type: 'customer_action',
    })
}
